import {
  Avatar,
  Chip,
  createStyles,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {AvailableAgentNumber} from '../UserAdministration/UserEntry/AgentNumberInput';
import './AgentNumberInput.sass';

type AgentNumberInputProps = {
  inputClassName?: string;
  className?: string;
  agentNumbers: ValidAgentNumber[];
  uniqueKey: string;
  onChangeAgentNumber: (agentNumber: ValidAgentNumber[]) => any;
};

const useStyles = makeStyles(() =>
  createStyles({
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    chip: {
      margin: 2,
    },
    input: {
      minHeight: 49,
    },
    selectedItem: {
      backgroundColor: 'rgba(0, 0, 0, 0.14)',
    },
  })
);

const AgentNumberInput = (props: AgentNumberInputProps) => {
  const classes = useStyles();
  const agentNumbers = useSelector((state: ReducerState) => state.user.agentNumber.agentNumbersDict[props.uniqueKey]);

  const uniqueValue = (agentNumber: AvailableAgentNumber) =>
    `${agentNumber.value!.toUpperCase()}${(agentNumber.isNewYork && ' - NY') || ''}`;

  const agentNumberAlreadyExist = (toFind: AvailableAgentNumber) =>
    !!props.agentNumbers.find(
      validAgentNumber => validAgentNumber.value === toFind.value && validAgentNumber.isNewYork === toFind.isNewYork
    );

  const getAgentNumberStatus = (statusId: AgentNumberStatusId) =>
    statusId === AgentNumberStatusId.Inactive
      ? ' - (Inactive)'
      : statusId === AgentNumberStatusId.Terminated
      ? ' - (Terminated)'
      : '';

  const matchAgentNumbers = (agentNumber: AvailableAgentNumber) =>
    agentNumbers?.find(an => an.value === agentNumber.value && an.isNewYork === agentNumber.isNewYork) || agentNumber;

  return (
    <div className={classNames('agent-number-input', props.className)}>
      <FormControl variant="outlined" fullWidth>
        {(((agentNumbers && agentNumbers.length > 0) || props.agentNumbers.length > 0) && (
          <>
            <InputLabel htmlFor="agent-numbers">Agent Numbers</InputLabel>
            <Select
              multiple
              displayEmpty
              fullWidth
              value={props.agentNumbers}
              onChange={e => {
                const list = e.target.value as AvailableAgentNumber[];
                const lastItem = list[list.length - 1];
                let newList = list.map(agentNumber =>
                  ValidAgentNumber.create(agentNumber.value!, agentNumber.isNewYork!, agentNumber.status)
                );

                if (list.length > 1 && agentNumberAlreadyExist(lastItem)) {
                  newList = newList.filter(
                    agentNumber => agentNumber.value !== lastItem.value || agentNumber.isNewYork !== lastItem.isNewYork
                  );
                }
                props.onChangeAgentNumber(newList as ValidAgentNumber[]);
              }}
              input={<OutlinedInput fullWidth labelWidth={125} className={classes.input} />}
              renderValue={selectedAgentNumbers => (
                <div className={classes.chips}>
                  {(selectedAgentNumbers as AvailableAgentNumber[]).map(agentNumber => {
                    const mappedAgentNumber = matchAgentNumbers(agentNumber);

                    return (
                      <Chip
                        color="primary"
                        key={uniqueValue(mappedAgentNumber)}
                        avatar={(mappedAgentNumber.isNewYork ? <Avatar>NY</Avatar> : null) as any}
                        label={
                          <>
                            <span>{mappedAgentNumber.value}</span>
                            <em>{getAgentNumberStatus(mappedAgentNumber.status.id)}</em>
                          </>
                        }
                        className={classes.chip}
                      />
                    );
                  })}
                </div>
              )}
              disabled={!agentNumbers || agentNumbers.length === 0}
            >
              {agentNumbers &&
                agentNumbers.map(agentNumber => (
                  <MenuItem
                    key={uniqueValue(agentNumber)}
                    value={agentNumber as any}
                    className={agentNumberAlreadyExist(agentNumber) ? classes.selectedItem : ''}
                  >
                    {uniqueValue(agentNumber)}
                  </MenuItem>
                ))}
            </Select>
          </>
        )) || (
          <OutlinedInput
            fullWidth
            labelWidth={125}
            className={classes.input}
            disabled
            placeholder="No Agent Numbers Available"
          />
        )}
      </FormControl>
    </div>
  );
};

export default AgentNumberInput;
